import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { focusNextField } from '../utils/focusNext';
import { useRecentPicks } from '../utils/recentPicks';
import { RecentPickLabel, RecentPickDivider } from './RecentPickLabel';

const SearchSelect = ({
    options = [],
    value = '',
    onChange,
    placeholder = 'Search...',
    displayKey = 'name',
    secondaryKey = '',
    valueKey = '_id',
    icon = faSearch,
    disabled = false,
    // Which recent picks to show first (utils/recentPicks.js), e.g. 'expense-vendor'.
    recentKey
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const recent = useRecentPicks(recentKey, isOpen);

    // Check if options are just flat strings instead of objects
    const isStringArray = options.length > 0 && typeof options[0] === 'string';

    const selectedOption = isStringArray
        ? options.find(opt => opt === value)
        : options.find(opt => opt[valueKey] === value);

    // 👇 FIXED: Wrapped in useCallback to satisfy React's exhaustive-deps
    const getPrimaryText = useCallback((opt) => {
        if (!opt) return '';
        if (isStringArray) return String(opt);
        return typeof displayKey === 'function' ? displayKey(opt) : String(opt[displayKey] || '');
    }, [isStringArray, displayKey]);

    // 👇 FIXED: Wrapped in useCallback
    const getSecondaryText = useCallback((opt) => {
        if (isStringArray || !secondaryKey || !opt) return '';
        return String(opt[secondaryKey] || '');
    }, [isStringArray, secondaryKey]);

    // Auto-fill input if a value is pre-selected (like on Edit Pages)
    useEffect(() => {
        if (selectedOption && !isOpen) {
            setSearchTerm(getPrimaryText(selectedOption));
        } else if (!selectedOption && !isOpen) {
            setSearchTerm('');
        }
    }, [isOpen, selectedOption, getPrimaryText]); // 👇 FIXED: Added correct dependencies

    // Handle clicking outside the dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                if (selectedOption) {
                    setSearchTerm(getPrimaryText(selectedOption));
                } else {
                    setSearchTerm('');
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [selectedOption, getPrimaryText]); // 👇 FIXED: Added correct dependencies

    const filteredOptions = options.filter(opt => {
        const primaryText = getPrimaryText(opt).toLowerCase();
        const secondaryText = getSecondaryText(opt).toLowerCase();
        const search = searchTerm.toLowerCase();
        return primaryText.includes(search) || secondaryText.includes(search);
    });

    // With nothing typed, the ones picked here lately come first. Remembered by
    // id where there is one, so a renamed vendor or project still counts.
    const recentIdOf = (opt) => (isStringArray ? opt : (opt._id || opt[valueKey]));
    const { recent: recentRows, rest: otherRows } = searchTerm.trim()
        ? { recent: [], rest: filteredOptions }
        : recent.split(filteredOptions, recentIdOf);
    const listed = [...recentRows, ...otherRows];

    return (
        <div style={{ position: 'relative', width: '100%' }} ref={dropdownRef}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <FontAwesomeIcon icon={icon} style={{ position: 'absolute', left: '12px', color: 'light-dark(#94a3b8, var(--theme-subtle))' }} />
                <input
                    type="text"
                    className="custom-input m-0"
                    placeholder={placeholder}
                    disabled={disabled}
                    style={{
                        paddingLeft: '35px', paddingRight: '30px',
                        borderColor: value ? '#16a34a' : 'light-dark(#cbd5e1, var(--theme-border-strong, #4a4e55))',
                        background: disabled ? 'light-dark(#f1f5f9, var(--theme-surface-raised, #292c31))' : 'light-dark(#fff, var(--theme-surface, #1a1c1f))',
                        cursor: disabled ? 'not-allowed' : 'text'
                    }}
                    value={isOpen ? searchTerm : (selectedOption ? getPrimaryText(selectedOption) : searchTerm)}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        onChange(''); // Clear selection immediately when typing to force re-selection
                        setIsOpen(true);
                    }}
                    onFocus={() => {
                        if (!disabled) { 
                            setIsOpen(true); 
                            setSearchTerm(''); // Clear to show all options
                        }
                    }}
                />
                <FontAwesomeIcon icon={faChevronDown} style={{ position: 'absolute', right: '12px', color: 'light-dark(#94a3b8, var(--theme-subtle))', pointerEvents: 'none' }} />
            </div>

            {isOpen && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: '4px',
                    background: 'light-dark(#fff, var(--theme-surface))', border: '1px solid light-dark(#e2e8f0, var(--theme-border))', borderRadius: '6px',
                    maxHeight: '220px', overflowY: 'auto', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}>
                    {listed.length > 0 ? (
                        listed.map((opt, index) => {
                            const val = isStringArray ? opt : opt[valueKey];
                            const isSelected = value === val;
                            
                            return (
                                <React.Fragment key={val}>
                                {index === 0 && recentRows.length > 0 && <RecentPickLabel />}
                                {index === recentRows.length && index > 0 && <RecentPickDivider />}
                                <div
                                    style={{
                                        padding: '10px 15px', cursor: 'pointer',
                                        borderBottom: '1px solid light-dark(#f8fafc, var(--theme-border, #33363b))', fontSize: '14px',
                                        background: isSelected ? 'light-dark(#f0fdf4, var(--theme-success-soft, #1a3a31))' : 'light-dark(#fff, var(--theme-surface, #1a1c1f))'
                                    }}
                                    onMouseDown={(e) => {
                                        e.preventDefault(); // Prevents input blur from firing before click registers
                                        recent.record(recentIdOf(opt));
                                        onChange(val);
                                        setSearchTerm(getPrimaryText(opt));
                                        setIsOpen(false);
                                        // One answer is all this needs: carry on to the next field.
                                        setTimeout(() => focusNextField(dropdownRef.current), 0);
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? '#f0fdf4' : 'white'}
                                >
                                    <div style={{ fontWeight: '600', color: 'light-dark(#0f172a, var(--theme-ink))' }}>{getPrimaryText(opt)}</div>
                                    {!isStringArray && getSecondaryText(opt) && (
                                        <div style={{ fontSize: '12px', color: 'light-dark(#64748b, var(--theme-muted))', marginTop: '2px' }}>
                                            {secondaryKey === 'gstNumber' ? 'GST: ' : ''}{getSecondaryText(opt)}
                                        </div>
                                    )}
                                </div>
                                </React.Fragment>
                            );
                        })
                    ) : (
                        <div style={{ padding: '15px', color: 'light-dark(#64748b, var(--theme-muted))', fontSize: '14px', textAlign: 'center' }}>No matches found</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchSelect;